import axios from 'axios'
import apiClient from './apiClient'

/** A single entry returned by the listDirectory API. */
export interface FileEntry {
  name:  string
  path:  string        // absolute path on the IDE server (for user repos) or workspace-relative
  isDir: boolean
  size:  number | null // null for directories
  mtime: number        // Unix timestamp of last modification
}

// Absolute paths (starting with '/') live on the IDE server — route to Node.js.
// Relative paths go to PHP on evelyn via the standard apiClient.
function isNodePath(p: string): boolean {
  return p.startsWith('/')
}

function nodeAuthHeader(): Record<string, string> {
  const token = (window as unknown as Record<string, unknown>).__CRAFT_SESSION_TOKEN__ as string ?? ''
  return { Authorization: `Bearer ${token}` }
}

/**
 * List directory contents at the given path.
 * Returns entries sorted by the server: directories first, then files alphabetically.
 */
export async function listDirectory(filePath: string): Promise<FileEntry[]> {
  if (isNodePath(filePath)) {
    const { data } = await axios.get<{ items: FileEntry[] }>('/node-api/files', {
      params:  { path: filePath },
      headers: nodeAuthHeader(),
    })
    return data.items
  }
  const { data } = await apiClient.get<{ items: FileEntry[] }>(
    '/api/ide/files',
    { params: { path: filePath } },
  )
  return data.items
}

/**
 * Read the full UTF-8 content of a file at the given path.
 */
export async function readFile(filePath: string): Promise<string> {
  if (isNodePath(filePath)) {
    const { data } = await axios.get<{ content: string }>('/node-api/files/read', {
      params:  { path: filePath },
      headers: nodeAuthHeader(),
    })
    return data.content
  }
  const { data } = await apiClient.get<{ content: string; path: string }>(
    '/api/ide/files/read',
    { params: { path: filePath } },
  )
  return data.content
}

/**
 * Write content to a file. Creates intermediate directories on the server if needed.
 */
export async function writeFile(filePath: string, content: string): Promise<void> {
  if (isNodePath(filePath)) {
    await axios.post('/node-api/files/write', { path: filePath, content }, { headers: nodeAuthHeader() })
    return
  }
  await apiClient.post('/api/ide/files/write', { path: filePath, content })
}

/**
 * Permanently delete a file. Directories are rejected by the server.
 */
export async function deleteFile(filePath: string): Promise<void> {
  if (isNodePath(filePath)) {
    await axios.post('/node-api/files/delete', { path: filePath }, { headers: nodeAuthHeader() })
    return
  }
  await apiClient.post('/api/ide/files/delete', { path: filePath })
}

/**
 * Create a directory (and all intermediate parents) at the given path.
 */
export async function createFolder(folderPath: string): Promise<void> {
  if (isNodePath(folderPath)) {
    await axios.post('/node-api/files/mkdir', { path: folderPath }, { headers: nodeAuthHeader() })
    return
  }
  await apiClient.post('/api/ide/files/mkdir', { path: folderPath })
}

/**
 * Create an empty file at the given path. Fails with 409 if the file already exists.
 */
export async function createFile(filePath: string): Promise<void> {
  if (isNodePath(filePath)) {
    await axios.post('/node-api/files/touch', { path: filePath }, { headers: nodeAuthHeader() })
    return
  }
  await apiClient.post('/api/ide/files/touch', { path: filePath })
}
